import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import styled from "styled-components";
import { postReservationAccept, updateReservationStatus, sendTableReadyMessage } from "../../actions/restaurantActions";
import { fetchCancelReservation, checkCancelAction } from "../../actions/reservationsActions";
import { statusToGroup, iconStatusMap, ModernButton } from "../DefaultImports";
import CompareChanges from "../CompareChanges";
import StatusDropdown from "../StatusDropdown";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faEye, faTimes, faCircleDot } from "@fortawesome/free-solid-svg-icons";
import { acceptedStatuses, pendingStatuses, getReservationStatusAbreviationTranslation, getReservationStatusTooltipTranslationForRestaurant } from "../ReservationStatus";
import Tooltip from "../ToolTipWrapper";
import { CustomModal } from "../CustomModal";
const DEFAULT_STATUS_COLOR = "#6b8c88";
const ICON_COLOR_VARIATION_STORAGE_KEY = "reservation_icon_color_variation";
const ICON_COLOR_VARIATION_CHANGE_EVENT = "reservation_icon_color_variation_change";
const STATUS_COLORS = {
  "A CHEGAR": "#2F80ED",
  ATRASADA: "#D97706",
  "À PORTA": "#7C3AED",
  REST: "#0F766E",
  CLIENTE: "#2563EB",
  ESPERA: "#B45309",
  FINALIZADA: "#64748B",
  "Mesa livre": "#16A34A",
  CANCELADAS: "#DC2626",
  TOTAL: "#334155"
};
const getStoredIconColorVariation = () => {
  if (typeof window === "undefined") return 2;
  const storedValue = window.localStorage.getItem(ICON_COLOR_VARIATION_STORAGE_KEY);
  if (storedValue === "1") return 1;
  if (storedValue === "2") return 2;
  return 2;
};
const getVariationColor = (iconData, selectedColorVariation, statusGroup) => {
  const variationColor = iconData?.[`color_variation_${selectedColorVariation}`]?.trim?.();
  if (variationColor) return variationColor;
  const normalColor = iconData?.color?.trim?.();
  if (normalColor) return normalColor;
  return STATUS_COLORS[statusGroup] || DEFAULT_STATUS_COLOR;
};
const LineWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: start;
  background: #ffffff;
  color: #99b0ad;
  padding: 0 0 0 6px;
  width: 100%;
  min-height: 34px;
  border-top: 1px solid rgba(153, 176, 173, 0.18);
`;
const StatusIconSpan = styled.span`
  width: 28px;
  min-width: 28px;
  height: 34px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: ${({
  $color
}) => $color || DEFAULT_STATUS_COLOR};
  cursor: pointer;
  margin-right: 4px;

  svg {
    width: 21px;
    height: 21px;
    max-width: 21px;
    max-height: 21px;
    color: inherit;
  }

  svg path {
    fill: currentColor;
  }
`;
const StatusDropdownWrapper = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  color: #4b3335;
  font-size: 10px !important;

  button,
  select,
  span,
  div {
    color: #99b0ad !important;
  }

  svg {
    color: #99b0ad !important;
  }
`;
const IconButton = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  color: #99b0ad;
  width: 50%;
  min-height: 34px;
  cursor: pointer;
  transition:
    background-color 0.15s ease,
    color 0.15s ease,
    transform 0.1s ease;
  border-left: 1px solid rgba(153, 176, 173, 0.18);

  svg {
    width: 20px;
    height: 20px;
    max-width: 20px;
    max-height: 20px;
  }

  &:hover {
    background-color: rgba(238, 117, 76, 0.08);
    color: #ee754c;
    transform: scale(1.04);
  }
`;
const InBetween = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0;
`;
function ReservationStatusChanger({
  reservation,
  past_estimated_time
}) {
  const {
    t
  } = useTranslation();
  const dispatch = useDispatch();
  const [showAcceptModal, setShowAcceptModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showAttendedModal, setShowAttendedModal] = useState(false);
  const [showNotAttendedModal, setShowNotAttendedModal] = useState(false);
  const [showLeftModal, setShowLeftModal] = useState(false);
  const [rejectEdition, setRejectEdition] = useState(false);
  const [reservationBeforeEdition, setReservationBeforeEdition] = useState(null);
  const [notify_customer, setNotify_customer] = useState(true);
  const [sendTableReadyMsg, setSendTableReadyMsg] = useState(true);
  const [selectedColorVariation, setSelectedColorVariation] = useState(getStoredIconColorVariation);
  const currentStatusGroup = statusToGroup[reservation?.status];
  const currentIconData = iconStatusMap[currentStatusGroup] || {
    icon: faCircleDot,
    label: currentStatusGroup || reservation?.status || "Estado"
  };
  const currentStatusColor = getVariationColor(currentIconData, selectedColorVariation, currentStatusGroup);
  useEffect(() => {
    const updateColorVariation = event => {
      if (event?.type === "storage" && event.key !== ICON_COLOR_VARIATION_STORAGE_KEY) {
        return;
      }
      if (event?.type === ICON_COLOR_VARIATION_CHANGE_EVENT && (event.detail === 1 || event.detail === 2)) {
        setSelectedColorVariation(event.detail);
        return;
      }
      setSelectedColorVariation(getStoredIconColorVariation());
    };
    window.addEventListener("storage", updateColorVariation);
    window.addEventListener(ICON_COLOR_VARIATION_CHANGE_EVENT, updateColorVariation);
    return () => {
      window.removeEventListener("storage", updateColorVariation);
      window.removeEventListener(ICON_COLOR_VARIATION_CHANGE_EVENT, updateColorVariation);
    };
  }, []);
  useEffect(() => {
    if (reservation?.history && reservation.restore_version_id !== undefined) {
      const item = reservation.history.find(h => h.history_id === reservation.restore_version_id);
      setReservationBeforeEdition(item || null);
    } else {
      setReservationBeforeEdition(null);
    }
  }, [reservation]);
  const handleAccept = () => {
    dispatch(postReservationAccept(reservation.token, false, rejectEdition, notify_customer));
    setShowAcceptModal(false);
  };
  const handleCancelCheck = () => {
    dispatch(checkCancelAction(reservation.token));
    setShowAcceptModal(false);
  };
  const handleCancel = () => {
    dispatch(fetchCancelReservation(reservation.id, false, false, rejectEdition));
    setShowCancelModal(false);
  };
  const handleNotAttended = () => {
    dispatch(updateReservationStatus(reservation.id, "no_show"));
    setShowNotAttendedModal(false);
  };
  const handleLeft = () => {
    dispatch(updateReservationStatus(reservation.id, "completed"));
    setShowLeftModal(false);
  };
  const isWaitingList = reservation?.status === "waiting_list";
  useEffect(() => {
    if (showAttendedModal && isWaitingList) {
      setSendTableReadyMsg(true);
    }
  }, [showAttendedModal, isWaitingList]);
  const handleAttendedWithOptionalMessage = () => {
    dispatch(updateReservationStatus(reservation.id, "attended"));
    if (reservation?.status === "waiting_list" && sendTableReadyMsg) {
      dispatch(sendTableReadyMessage(reservation.token));
    }
    setShowAttendedModal(false);
  };
  return <LineWrapper>
      <Tooltip text={getReservationStatusTooltipTranslationForRestaurant(reservation.status)}>
        <StatusIconSpan $color={currentStatusColor}>
          <FontAwesomeIcon icon={currentIconData.icon} />
        </StatusIconSpan>
      </Tooltip>

      <InBetween>
        <StatusDropdownWrapper>
          <StatusDropdown reservation={reservation} allowedStatuses={["accepted", "pending_restaurant", "late", "waiting_list", "attended", "completed", "no_show", "cancelled_restaurant", "cancelled_customer"]} getStatusColor={() => currentStatusColor} getStatusLabel={getReservationStatusAbreviationTranslation} onSelectStatus={(status, options = {}) => {
          dispatch(updateReservationStatus(reservation.id, status, options));
        }} />
        </StatusDropdownWrapper>

        <div style={{
        height: "100%",
        width: "150px",
        display: "flex",
        justifyContent: "end"
      }}>
          {pendingStatuses.includes(reservation.status) && <>
              <IconButton onClick={() => {
            setRejectEdition(false);
            setShowAcceptModal(true);
          }}>
                <FontAwesomeIcon icon={faCheck} />
              </IconButton>

              <IconButton onClick={() => {
            setRejectEdition(false);
            setShowCancelModal(true);
          }}>
                <FontAwesomeIcon icon={faTimes} />
              </IconButton>
            </>}

          {reservation.status === "edited" && <>
              <IconButton onClick={() => {
            setRejectEdition(true);
            setShowAcceptModal(true);
          }}>
                <Tooltip text={getReservationStatusTooltipTranslationForRestaurant("Edited_accept")}>
                  <FontAwesomeIcon icon={faCheck} />
                </Tooltip>
              </IconButton>

              <IconButton onClick={() => {
            setRejectEdition(true);
            setShowCancelModal(true);
          }}>
                <Tooltip text={getReservationStatusTooltipTranslationForRestaurant("Edited_reject")}>
                  <FontAwesomeIcon icon={faTimes} />
                </Tooltip>
              </IconButton>
            </>}

          {acceptedStatuses.includes(reservation.status) && <>
              <IconButton onClick={() => setShowAttendedModal(true)}>
                <FontAwesomeIcon icon={faCheck} />
              </IconButton>

              <IconButton onClick={() => setShowNotAttendedModal(true)}>
                <FontAwesomeIcon icon={faTimes} />
              </IconButton>
            </>}

          {reservation.status === "attended" && <IconButton onClick={() => setShowLeftModal(true)} style={{
          width: "100%"
        }}>
              <FontAwesomeIcon icon={faCheck} />
            </IconButton>}

          {reservation.cancelled_warning && <IconButton onClick={() => handleCancelCheck()} style={{
          width: "100%"
        }}>
              <FontAwesomeIcon icon={faEye} />
            </IconButton>}
        </div>
      </InBetween>

      <CustomModal show={showAcceptModal} onHide={() => setShowAcceptModal(false)} title={t("ui.components.reservations.reservationStatusChanger.aceitarReserva")} body={rejectEdition && reservationBeforeEdition ? <CompareChanges before={reservationBeforeEdition} after={reservation} /> : <p>{t("ui.components.reservations.reservationStatusChanger.tensACertezaQuePretendesAceitar")}</p>} footer={<>
            <ModernButton color="#99b0ad" fontSize="14px" onClick={() => setShowAcceptModal(false)}>{t("ui.components.reservations.reservationStatusChanger.cancelar")}</ModernButton>
            <ModernButton color="#EE754C" fontSize="14px" onClick={handleAccept}>{t("ui.components.reservations.reservationStatusChanger.aceitarReserva2")}</ModernButton>
          </>} />

      <CustomModal show={showCancelModal} onHide={() => setShowCancelModal(false)} title={t("ui.components.reservations.reservationStatusChanger.rejeitarReserva")} body={rejectEdition && reservationBeforeEdition ? <CompareChanges before={reservationBeforeEdition} after={reservation} /> : <p>{t("ui.components.reservations.reservationStatusChanger.tensACertezaQuePretendesRejeitar")}</p>} footer={<>
            <ModernButton color="#99b0ad" fontSize="14px" onClick={() => setShowCancelModal(false)}>{t("ui.components.reservations.reservationStatusChanger.cancelar2")}</ModernButton>
            <ModernButton color="#EE754C" fontSize="14px" onClick={handleCancel}>{t("ui.components.reservations.reservationStatusChanger.rejeitarReserva2")}</ModernButton>
          </>} />

      <CustomModal show={showAttendedModal} onHide={() => setShowAttendedModal(false)} title={t("ui.components.reservations.reservationStatusChanger.confirmarPresenca")} body={<>
            <p>{t("ui.components.reservations.reservationStatusChanger.tensACertezaQuePretendesAlterar")}{" "}
              <strong>{t("ui.components.reservations.reservationStatusChanger.compareceu")}</strong>?
            </p>

            {isWaitingList && <label style={{
        display: "flex",
        alignItems: "center",
        gap: "10px",
        marginTop: "12px",
        cursor: "pointer",
        userSelect: "none"
      }}>
                <input type="checkbox" checked={sendTableReadyMsg} onChange={e => setSendTableReadyMsg(e.target.checked)} style={{
          accentColor: "#EE754C"
        }} />
                <span style={{
          fontWeight: 700
        }}>{t("ui.components.reservations.reservationStatusChanger.enviarMensagemDeAvisoAoCliente")}</span>
              </label>}
          </>} footer={<>
            <ModernButton color="#99b0ad" fontSize="14px" onClick={() => setShowAttendedModal(false)}>{t("ui.components.reservations.reservationStatusChanger.naoConfirmar")}</ModernButton>

            <ModernButton color="#EE754C" fontSize="14px" onClick={handleAttendedWithOptionalMessage}>{t("ui.components.reservations.reservationStatusChanger.confirmarPresenca2")}</ModernButton>
          </>} />

      <CustomModal show={showNotAttendedModal} onHide={() => setShowNotAttendedModal(false)} title={t("ui.components.reservations.reservationStatusChanger.confirmarAusencia")} body={<p>{t("ui.components.reservations.reservationStatusChanger.tensACertezaQuePretendesAlterar2")}{" "}
            <strong>{t("ui.components.reservations.reservationStatusChanger.naoCompareceu")}</strong>?
          </p>} footer={<>
            <ModernButton color="#99b0ad" fontSize="14px" onClick={() => setShowNotAttendedModal(false)}>{t("ui.components.reservations.reservationStatusChanger.naoConfirmar2")}</ModernButton>
            <ModernButton color="#EE754C" fontSize="14px" onClick={handleNotAttended}>{t("ui.components.reservations.reservationStatusChanger.confirmarAusencia2")}</ModernButton>
          </>} />

      <CustomModal show={showLeftModal} onHide={() => setShowLeftModal(false)} title={t("ui.components.reservations.reservationStatusChanger.libertarMesa")} body={<p>{t("ui.components.reservations.reservationStatusChanger.tensACertezaQuePretendesLibertar")}</p>} footer={<>
            <ModernButton color="#99b0ad" fontSize="14px" onClick={() => setShowLeftModal(false)}>{t("ui.components.reservations.reservationStatusChanger.nao")}</ModernButton>
            <ModernButton color="#EE754C" fontSize="14px" onClick={handleLeft}>{t("ui.components.reservations.reservationStatusChanger.sim")}</ModernButton>
          </>} />
    </LineWrapper>;
}
export default ReservationStatusChanger;
